import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import { ButtonV3 } from 'components/ComponentV2';
import {
  SchoolDetailsType,
  UniversityDetailsType,
} from 'hooks/useCandidateProfile';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { useAuth } from 'lib/providers/AuthProvider';

import { formatDate } from 'utils/format';
import EmptyBottomSection from '../EmptyBottomSection';
import FilledBottomSection from '../FilledBottomSection';
import TopTitleBar from '../TopTitleBar';

import styles from '../Profile.common.module.scss';
import { AddEducationModal } from './AddEducationModal.component';
import { getSchoolInfo, getUniversityInfo } from './utils';

export const Education = ({ className }: { className?: string }) => {
  const [isLastUniversity, setIsLastUniversity] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUniversityInfo, setSelectedUniversityInfo] =
    useState<UniversityDetailsType | null>(null);
  const [selectedSchoolInfo, setSelectedSchoolInfo] =
    useState<SchoolDetailsType | null>(null);
  const [t] = useTranslation('candidate.profile');

  const [isLastSchool, setIsLastSchool] = useState(false);
  const { educationComplete } = useCandidateProfile();

  const { candidate } = useAuth();
  const { graduation_year } = candidate || {};

  const getUniversityDetails = (item: UniversityDetailsType) => {
    const startDateFormatted = item?.start_date
      ? formatDate(new Date(item.start_date))
      : '';
    const endDateFormatted = item?.currently_studying
      ? t('education.ongoing')
      : item?.end_date
      ? formatDate(new Date(item.end_date))
      : '';

    const titleFields = [];
    if (item?.degree?.name) {
      titleFields.push(item.degree.name);
    }
    if (item?.subject?.name) {
      titleFields.push(item.subject.name);
    }
    if (item?.university?.name) {
      titleFields.push(item.university.name);
    }
    const title = titleFields.join(', ');
    const type = item?.type;

    return {
      title,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      description: item?.description || '',
      id: item?.id || '',
      type,
    };
  };

  const getSchoolDetails = (item: SchoolDetailsType) => {
    const startDateFormatted = item?.start_date
      ? formatDate(new Date(item.start_date))
      : '';
    const endDateFormatted = item?.end_date
      ? formatDate(new Date(item.end_date))
      : '';
    const type = item?.type;

    return {
      title: `${item?.school_type}, ${item?.school_name}`,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      description: item?.description || '',
      id: item?.id || '',
      type,
    };
  };

  const [universityInfo, setUniversityInfo] = useState<UniversityDetailsType[]>(
    []
  );
  const [schoolInfo, setSchoolInfo] = useState<SchoolDetailsType[]>([]);

  useEffect(() => {
    if (candidate) {
      setUniversityInfo(getUniversityInfo(candidate));

      setSchoolInfo(getSchoolInfo(candidate));
    }
  }, [candidate]);
  const dataForUniversity = universityInfo.map(getUniversityDetails);
  const dataForSchool = schoolInfo.map(getSchoolDetails);
  useEffect(() => {
    const length = dataForSchool.length + dataForUniversity.length;
    if (length === dataForUniversity.length) {
      setIsLastUniversity(true);
    } else {
      setIsLastUniversity(false);
      setIsLastSchool(true);
    }
  }, [dataForSchool, dataForUniversity]);

  const addEducation = useCallback(() => {
    setSelectedUniversityInfo(null);
    setSelectedSchoolInfo(null);
    setModalOpen(true);
  }, []);

  const onEditClick = (id: string, type: string) => {
    setSelectedUniversityInfo(null);
    setSelectedSchoolInfo(null);
    if (type === 'University') {
      const info = universityInfo.find((item) => item.id === id);
      if (info) setSelectedUniversityInfo(info);
    } else if (type === 'School') {
      const info = schoolInfo.find((item) => item.id === id);
      if (info) setSelectedSchoolInfo(info);
    }
    setModalOpen(true);
  };

  return (
    <article className={className}>
      <TopTitleBar
        title={t('education.title')}
        isInfoComplete={educationComplete}
        iconName="icon_plus"
        onIconClick={addEducation}
      />
      {educationComplete ? (
        <section className={styles.content}>
          {dataForUniversity.map((item, index) => (
            <FilledBottomSection
              id={item.id}
              key={index}
              title={item.title}
              startDate={item.startDate}
              endDate={item.endDate}
              description={item.description}
              isLast={
                isLastUniversity && dataForUniversity.length - 1 === index
              }
              onEditClick={() => onEditClick(item.id, item.type)}
              graduationYear={graduation_year?.toString()}
            />
          ))}
          {dataForSchool.map((item, index) => (
            <FilledBottomSection
              key={index}
              id={item.id}
              title={item.title}
              startDate={item.startDate}
              endDate={item.endDate}
              description={item.description}
              isLast={isLastSchool && dataForSchool.length - 1 === index}
              onEditClick={() => onEditClick(item.id, item.type)}
            />
          ))}

          <ButtonV3
            onClick={addEducation}
            startIcon="icon_plus"
            color="secondary"
            size="small"
          >
            {t('education.button-text')}{' '}
          </ButtonV3>
        </section>
      ) : (
        <EmptyBottomSection
          desc={t('education.desc')}
          addButtonText={t('education.button-text')}
          createWithAIButton={false}
          onAddIconClick={addEducation}
        />
      )}
      {isModalOpen ? (
        <AddEducationModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          isEdit={
            selectedUniversityInfo === null && selectedSchoolInfo === null
              ? false
              : true
          }
          universityInfo={selectedUniversityInfo}
          schoolInfo={selectedSchoolInfo}
        />
      ) : null}
    </article>
  );
};
